import { Alert, Spin, Typography } from 'antd'
import { FC } from 'react'
import styled from 'styled-components'

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.25rem 0;
`

const Label = styled.span`
  min-width: 11rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.875rem;
`

const Value = styled.span`
  font-family:
    ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const StatusRow = styled.div<{ $pass: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin-top: 0.5rem;
  border-radius: 4px;
  background: ${({ $pass }) => ($pass ? '#f6ffed' : '#fff2f0')};
  border: 1px solid ${({ $pass }) => ($pass ? '#b7eb8f' : '#ffccc7')};
  color: ${({ $pass }) => ($pass ? '#389e0d' : '#cf1322')};
  font-weight: 600;
`

export interface ObservedMinThicknessCheckPanelProps {
  tMinMm: number | null
  observedMinMm: number | null
  pass: boolean | null
  isLoading: boolean
  noCdf: boolean
}

const fmtMm = (n: number | null): string =>
  n === null ? '--' : `${n.toFixed(2)} mm`

/**
 * Compares the inspection's observed minimum thickness (sourced from the
 * thickness CDF on the CDS) against the computed `t_min` for the Minimum
 * Thickness Analysis step. Pure presentational -- the caller passes in
 * pre-computed values.
 */
export const ObservedMinThicknessCheckPanel: FC<
  ObservedMinThicknessCheckPanelProps
> = ({ tMinMm, observedMinMm, pass, isLoading, noCdf }) => {
  if (isLoading && observedMinMm === null) {
    return (
      <div>
        <Spin size='small' /> Loading observed minimum...
      </div>
    )
  }

  if (noCdf) {
    return (
      <Alert
        type='info'
        showIcon
        message='No thickness CDF available for this inspection.'
        description='The check needs a thickness component-data-frame produced by Conduit. Once the inspection has thickness data, this section will populate.'
      />
    )
  }

  return (
    <>
      <Row>
        <Label>Required (t_min)</Label>
        <Value>{fmtMm(tMinMm)}</Value>
      </Row>
      <Row>
        <Label>Observed minimum (CDS)</Label>
        <Value>{fmtMm(observedMinMm)}</Value>
      </Row>
      {pass !== null && observedMinMm !== null && tMinMm !== null ? (
        <StatusRow $pass={pass}>
          {pass ? 'PASS' : 'FAIL'} -- observed minimum {pass ? '>=' : '<'}{' '}
          required t_min
        </StatusRow>
      ) : observedMinMm === null ? (
        <Typography.Text type='secondary'>
          Waiting for thickness statistics to load...
        </Typography.Text>
      ) : null}
    </>
  )
}
